import { DEFAULT_MEETING_CONFIG, TARGET_APP_LOCALES, TimezoneValues, } from '@govinity/shared-config';
import { z } from 'zod';
import { addressRequiredSchema } from '../addresses/index.js';
import { phoneRequiredSchema } from '../phones/index.js';
export const QuorumRule = {
    Majority: 'majority',
    TwoThirds: 'two_thirds',
    Unanimous: 'unanimous',
};
export { TimezoneValues };
const bannerSizeSchema = z.object({
    x: z.number(),
    y: z.number(),
    width: z.number(),
    height: z.number(),
});
const governmentBaseSchema = z.object({
    name: z.string('Name is required').min(1, 'Name is required'),
    code: z
        .string('Code is required')
        .min(1, 'Code is required')
        .max(50, 'Code must be less than 50 characters')
        .regex(/^[a-z0-9-]+$/, 'Code can only contain lowercase letters, numbers, and hyphens'),
    website: z.url('Invalid website URL').nullable().optional(),
    logoUrl: z.url('Invalid logo URL').nullable().optional(),
    bannerUrl: z.url('Invalid banner URL').nullable().optional(),
    address: addressRequiredSchema,
    phone: phoneRequiredSchema,
    timezone: z.enum(TimezoneValues, { message: 'Invalid timezone value' }),
    termExpirationDays: z.number('Term expiration days is required').optional(),
    applicationOpenDays: z.number('Application open days is required').optional(),
    quorumRule: z.enum(QuorumRule).optional(),
    vacancyNoticeDays: z.number('Vacancy notice days is required').optional(),
    meetingConfig: z
        .object({
        votes: z.object({
            aye: z.string().min(1, 'Required').max(50),
            nay: z.string().min(1, 'Required').max(50),
            abstain: z.string().min(1, 'Required').max(50),
            recuse: z.string().min(1, 'Required').max(50),
            absent: z.string().min(1, 'Required').max(50),
        }),
        speakerTimer: z.object({
            fullsize: z.boolean(),
            finishSoundUrl: z.url('Invalid speaker finish sound URL').nullish(),
            presets: z
                .array(z
                .object({
                defaultSeconds: z.number().int().positive().max(3600),
                firstThreshold: z.number().int().positive().max(3600),
                secondThreshold: z.number().int().positive().max(3600),
            })
                .refine((data) => data.firstThreshold > data.secondThreshold, {
                message: 'First warning threshold must be greater than second warning threshold',
                path: ['firstThreshold'],
            }))
                .min(1, 'At least one speaker timer preset is required')
                .max(10),
            addTimeOptions: z.array(z.number().int().positive().max(3600)).min(1).max(10),
        }),
        publicSpeakers: z.object({
            enabled: z.boolean(),
            defaultSeconds: z
                .number()
                .int()
                .positive()
                .describe('The default number of seconds for the public speaker timer.'),
            signupOpenThreshold: z.number().int().positive().describe('Deprecated'),
            signupCloseThreshold: z
                .number()
                .int()
                .nonnegative()
                .describe('The number of seconds before the meeting starts that the public speaker signup window closes.'),
        }),
        publicComments: z.object({
            enabled: z.boolean(),
            allowAttachments: z.boolean(),
            signupCloseThreshold: z
                .number()
                .int()
                .nonnegative()
                .describe('The number of seconds before the meeting starts that the public comment signup window closes.'),
        }),
        display: z.object({
            theme: z.enum(['light', 'dark']),
            background: z.object({
                url: z.url('Invalid background image URL').nullish(),
                opacity: z.number().int().min(0).max(100),
                grayscale: z.boolean(),
                invert: z.boolean(),
                size: z.enum(['contain', 'cover']),
            }),
            showRecommendedAction: z.boolean(),
            showMotionMade: z.boolean(),
            motionResultsTime: z.number().int().nonnegative().max(3600),
        }),
    })
        .optional(),
    customizations: z
        .object({
        headerText: z.string().max(100, 'Header text must be less than 100 characters').nullish(),
        subheaderText: z
            .string()
            .max(100, 'Subheader text must be less than 100 characters')
            .nullish(),
        navLabels: z
            .object({
            home: z.string().max(50).nullish(),
            commission: z.string().max(50).nullish(),
            meetings: z.string().max(50).nullish(),
            boards: z.string().max(50).nullish(),
            topics: z.string().max(50).nullish(),
            documents: z.string().max(50).nullish(),
        })
            .nullish(),
        navHiddenElements: z
            .object({
            home: z.boolean().optional(),
            commission: z.boolean().optional(),
            meetings: z.boolean().optional(),
            boards: z.boolean().optional(),
            topics: z.boolean().optional(),
            documents: z.boolean().optional(),
        })
            .nullish(),
        banner: z
            .object({
            xs: bannerSizeSchema,
            md: bannerSizeSchema,
            lg: bannerSizeSchema,
        })
            .nullish(),
        footer: z
            .object({
            facebookUrl: z
                .string()
                .refine((val) => !val || z.string().url().safeParse(val).success, {
                message: 'Invalid Facebook URL',
            })
                .nullish(),
            linkedinUrl: z
                .string()
                .refine((val) => !val || z.string().url().safeParse(val).success, {
                message: 'Invalid LinkedIn URL',
            })
                .nullish(),
            twitterUrl: z
                .string()
                .refine((val) => !val || z.string().url().safeParse(val).success, {
                message: 'Invalid X URL',
            })
                .nullish(),
            youtubeUrl: z
                .string()
                .refine((val) => !val || z.string().url().safeParse(val).success, {
                message: 'Invalid YouTube URL',
            })
                .nullish(),
        })
            .nullish(),
        pageHeadings: z
            .object({
            commission: z
                .object({
                title: z.string().max(200, 'Title must be less than 200 characters').nullish(),
                subtitle: z.string().max(500, 'Subtitle must be less than 500 characters').nullish(),
                showBanner: z.boolean().optional(),
            })
                .nullish(),
            meetings: z
                .object({
                title: z.string().max(200, 'Title must be less than 200 characters').nullish(),
                subtitle: z.string().max(500, 'Subtitle must be less than 500 characters').nullish(),
                showBanner: z.boolean().optional(),
            })
                .nullish(),
            topics: z
                .object({
                title: z.string().max(200, 'Title must be less than 200 characters').nullish(),
                subtitle: z.string().max(500, 'Subtitle must be less than 500 characters').nullish(),
                showBanner: z.boolean().optional(),
            })
                .nullish(),
            boards: z
                .object({
                title: z.string().max(200, 'Title must be less than 200 characters').nullish(),
                subtitle: z.string().max(500, 'Subtitle must be less than 500 characters').nullish(),
                showBanner: z.boolean().optional(),
            })
                .nullish(),
            documents: z
                .object({
                title: z.string().max(200, 'Title must be less than 200 characters').nullish(),
                subtitle: z.string().max(500, 'Subtitle must be less than 500 characters').nullish(),
                showBanner: z.boolean().optional(),
            })
                .nullish(),
        })
            .nullish(),
        meetingsDefaultView: z.enum(['calendar', 'list']).nullish(),
        hideMediaAndTranscript: z.boolean().nullish(),
        logoClickUrl: z.url('Invalid URL').nullable().optional(),
        customNavLink: z
            .object({
            label: z.string().max(30, 'Label must be less than 30 characters').nullish(),
            url: z.url().nullish(),
        })
            .nullish()
            .refine((data) => {
            if (!data)
                return true;
            const hasLabel = !!data.label;
            const hasUrl = !!data.url;
            return (hasLabel && hasUrl) || (!hasLabel && !hasUrl);
        }, {
            path: ['label'],
            message: 'Both label and URL must be provided or both must be empty',
        }),
    })
        .optional(),
    hasTenure: z.boolean().optional(),
    hasRecord: z.boolean().optional(),
    hasLive: z.boolean().optional(),
    hasAgenda: z.boolean().optional(),
    hasWordIntegration: z.boolean().optional(),
    hasDocumentCheckout: z.boolean().optional(),
    hasDocumentSigning: z.boolean().optional(),
    hasSignatures: z.boolean().optional(),
    translationLocales: z.array(z.enum(TARGET_APP_LOCALES)).optional(),
    hasAssignmentBasedTerms: z.boolean().optional(),
    seatApplicationAutoNumberingConfigId: z.number().nullable().optional(),
    disableEmails: z.boolean().optional(),
    disableItemEmails: z.boolean().optional(),
    disableMeetingEmails: z.boolean().optional(),
    disablePackageAttachmentLabel: z.boolean().optional(),
    disablePackageItemLabel: z.boolean().optional(),
    rejectEncryptedUploads: z.boolean().optional(),
    disableShortTitle: z.boolean().optional(),
    allowAgendaPlacementFromItemForm: z.boolean().optional(),
    summaryFieldLabel: z
        .string()
        .trim()
        .max(50, 'Summary field label must be less than 50 characters')
        .nullable()
        .optional(),
    gaMeasurementId: z
        .string()
        .max(20, 'GA Measurement ID must be less than 20 characters')
        .regex(/^G-[A-Z0-9]+$/, 'GA Measurement ID must be in the format G-XXXXXXXXXX')
        .nullable()
        .optional(),
});
const termExpirationDaysRefinement = (data) => {
    if (data.termExpirationDays !== undefined &&
        data.termExpirationDays !== null &&
        data.applicationOpenDays !== undefined &&
        data.applicationOpenDays !== null) {
        return data.termExpirationDays >= data.applicationOpenDays;
    }
    return true;
};
const termExpirationDaysRefinementMessage = {
    message: 'Expiring Soon days must be greater than or equal to Open Application Window days',
    path: ['termExpirationDays'],
};
// Modules Govinity staff turn on from Supportboard, most of them paid. A customer's own system
// admin updates through governmentUpdateSchema, which leaves these out so they can't be set
// by calling the API directly.
const STAFF_ONLY_MODULES = {
    hasAgenda: true,
    hasAssignmentBasedTerms: true,
    hasDocumentCheckout: true,
    hasDocumentSigning: true,
    hasLive: true,
    hasRecord: true,
    hasTenure: true,
    hasWordIntegration: true,
    translationLocales: true,
};
export const governmentCreateSchema = governmentBaseSchema.refine(termExpirationDaysRefinement, termExpirationDaysRefinementMessage);
export const governmentUpdateSchema = governmentBaseSchema
    .omit(STAFF_ONLY_MODULES)
    .partial()
    .refine(termExpirationDaysRefinement, termExpirationDaysRefinementMessage);
export const governmentStaffUpdateSchema = governmentBaseSchema
    .partial()
    .refine(termExpirationDaysRefinement, termExpirationDaysRefinementMessage);
export const governmentMeetingBodySettingsSchema = governmentBaseSchema
    .pick({
    termExpirationDays: true,
    applicationOpenDays: true,
    quorumRule: true,
    vacancyNoticeDays: true,
    seatApplicationAutoNumberingConfigId: true,
})
    .refine(termExpirationDaysRefinement, termExpirationDaysRefinementMessage);
/**
 * Resolves a government's raw `meetingConfig` JSON into a complete {@link GovernmentMeetingConfig},
 * filling any missing section or field from {@link DEFAULT_MEETING_CONFIG}. Use this anywhere the
 * config is read so callers always get every field (votes labels, speaker timer, display).
 *
 * @param raw - The raw `Government.meetingConfig` JSON value.
 * @returns The resolved meeting config (always complete).
 */
export const resolveMeetingConfig = (raw) => {
    const config = typeof raw === 'object' && raw !== null && !Array.isArray(raw)
        ? raw
        : {};
    return {
        votes: { ...DEFAULT_MEETING_CONFIG.votes, ...(config.votes ?? {}) },
        speakerTimer: {
            ...DEFAULT_MEETING_CONFIG.speakerTimer,
            ...(config.speakerTimer ?? {}),
            presets: config.speakerTimer?.presets ??
                DEFAULT_MEETING_CONFIG.speakerTimer.presets.map((preset) => ({ ...preset })),
            addTimeOptions: config.speakerTimer?.addTimeOptions ?? [
                ...DEFAULT_MEETING_CONFIG.speakerTimer.addTimeOptions,
            ],
        },
        publicSpeakers: {
            ...DEFAULT_MEETING_CONFIG.publicSpeakers,
            ...(config.publicSpeakers ?? {}),
        },
        publicComments: {
            ...DEFAULT_MEETING_CONFIG.publicComments,
            ...(config.publicComments ?? {}),
        },
        display: {
            ...DEFAULT_MEETING_CONFIG.display,
            ...(config.display ?? {}),
            background: {
                ...DEFAULT_MEETING_CONFIG.display.background,
                ...(config.display?.background ?? {}),
            },
        },
    };
};
